import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthPage } from "@/pages/AuthPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ProtectedLayout } from "@/components/ProtectedLayout";
import { AgendaPage } from "@/pages/AgendaPage";
import { CustomersPage } from "@/pages/CustomersPage";
import { ServicesPage } from "@/pages/ServicesPage";
import { StaffPage } from "@/pages/StaffPage";
import { ActivityPage } from "@/pages/ActivityPage";
import { RecoveryPage } from "@/pages/RecoveryPage";
import { OwnerAlertsPage } from "@/pages/OwnerAlertsPage";
import { CustomerInboxLabPage } from "@/pages/CustomerInboxLabPage";
import { BusinessHoursPage } from "@/pages/BusinessHoursPage";
import { TrashPage } from "@/pages/TrashPage";
import { PublicReminderConfirmPage } from "@/pages/PublicReminderConfirmPage";
import { PublicReminderCancelPage } from "@/pages/PublicReminderCancelPage";
import { PublicRecoveryConfirmPage } from "@/pages/PublicRecoveryConfirmPage";
import { CustomerReminderConfirmPage, CustomerReminderCancelPage } from "@/pages/CustomerReminderPage";
import { CustomerRecoveryConfirmPage } from "@/pages/CustomerRecoveryPage";
import { PublicBookingPage } from "@/pages/PublicBookingPage";
import { BusinessProfilePage } from "@/pages/BusinessProfilePage";
import { Toaster } from "@/components/ui/sonner";
import { SpaNavigationContext } from "@/components/SpaNavigationContext";

function OwnerGuard({ children }) {
  const { isOwner } = useAuth();
  return isOwner ? children : <Navigate to="/" replace />;
}

export function App() {
  return (
    <div className="app-shell" data-testid="app-shell">
      <AuthProvider>
        <BrowserRouter>
          <SpaNavigationContext />
          <Routes>
            <Route path="/login" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/customer/reminders/confirm/:token" element={<CustomerReminderConfirmPage />} />
            <Route path="/customer/reminders/cancel/:token" element={<CustomerReminderCancelPage />} />
            <Route path="/customer/recovery/confirm/:token" element={<CustomerRecoveryConfirmPage />} />
            <Route path="/book/:businessIdOrSlug" element={<PublicBookingPage />} />
            <Route path="/reminders/confirm/:token" element={<PublicReminderConfirmPage />} />
            <Route path="/reminders/cancel/:token" element={<PublicReminderCancelPage />} />
            <Route path="/recovery/confirm/:token" element={<PublicRecoveryConfirmPage />} />
            <Route element={<ProtectedLayout />}>
              <Route path="/" element={<AgendaPage />} />
              <Route path="/appointments/new" element={<AgendaPage mode="booking" />} />
              <Route
                path="/agenda-tools"
                element={
                  <OwnerGuard>
                    <AgendaPage mode="tools" />
                  </OwnerGuard>
                }
              />
              <Route
                path="/lab/availability"
                element={
                  <OwnerGuard>
                    <AgendaPage mode="availability-lab" />
                  </OwnerGuard>
                }
              />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route
                path="/staff"
                element={
                  <OwnerGuard>
                    <StaffPage />
                  </OwnerGuard>
                }
              />
              <Route
                path="/recovery"
                element={
                  <OwnerGuard>
                    <RecoveryPage />
                  </OwnerGuard>
                }
              />
              <Route
                path="/activity"
                element={
                  <OwnerGuard>
                    <ActivityPage />
                  </OwnerGuard>
                }
              />
              <Route
                path="/business-profile"
                element={
                  <OwnerGuard>
                    <BusinessProfilePage />
                  </OwnerGuard>
                }
              />
              <Route
                path="/business-hours"
                element={
                  <OwnerGuard>
                    <BusinessHoursPage />
                  </OwnerGuard>
                }
              />
              <Route
                path="/customer-inbox-lab"
                element={
                  <OwnerGuard>
                    <CustomerInboxLabPage />
                  </OwnerGuard>
                }
              />
              <Route
                path="/owner-alerts"
                element={
                  <OwnerGuard>
                    <OwnerAlertsPage />
                  </OwnerGuard>
                }
              />
              <Route
                path="/trash"
                element={
                  <OwnerGuard>
                    <TrashPage />
                  </OwnerGuard>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
